import React from "react";
import type { RichContent } from "@/types";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Lightbulb, 
  ThumbsUp, 
  ThumbsDown, 
  DollarSign, 
  Users, 
  GraduationCap,
  Heart,
  Lock,
  Sparkles,
  Link2,
  Workflow,
  BookOpen,
  Scale
} from "lucide-react";

interface RichContentSectionProps {
  richContent: RichContent;
}

const RichContentSection: React.FC<RichContentSectionProps> = ({ richContent }) => {
  if (!richContent) return null;

  return (
    <div className="space-y-6">
      {/* Short Summary */}
      {richContent.short_summary && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <p className="text-lg leading-relaxed">{richContent.short_summary}</p>
          </CardContent>
        </Card>
      )}

      {/* Capabilities */}
      {richContent.capabilities && richContent.capabilities.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lightbulb className="h-5 w-5 text-primary" />
              What It Does
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {richContent.capabilities.map((cap, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-foreground/70 mt-1.5">•</span>
                  <span className="text-foreground">{cap}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Info Grid: Pricing, Audience, Learning Curve */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {richContent.pricing && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <DollarSign className="h-4 w-4 text-emerald-500" />
                Pricing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{richContent.pricing}</p>
            </CardContent>
          </Card>
        )}
        {richContent.audience && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <Users className="h-4 w-4 text-blue-500" />
                Best For
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{richContent.audience}</p>
            </CardContent>
          </Card>
        )}
        {richContent.learning_curve && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <GraduationCap className="h-4 w-4 text-purple-500" />
                Learning Curve
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{richContent.learning_curve}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Use Cases - Two Column Layout */}
      {((richContent.general_use_cases && richContent.general_use_cases.length > 0) || 
        (richContent.ministry_use_cases && richContent.ministry_use_cases.length > 0)) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {richContent.general_use_cases && richContent.general_use_cases.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">General Use Cases</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {richContent.general_use_cases.map((uc, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <span className="text-muted-foreground">•</span>
                      <span>{uc}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          {richContent.ministry_use_cases && richContent.ministry_use_cases.length > 0 && (
            <Card className="border-amber-500/20 bg-amber-500/5">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Heart className="h-5 w-5 text-amber-500" />
                  Ministry Use Cases
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {richContent.ministry_use_cases.map((uc, idx) => (
                    <li key={idx} className="text-sm">
                      <p>{uc.useCase}</p>
                      {uc.worksOfMercy && uc.worksOfMercy.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {uc.worksOfMercy.map((wom, widx) => (
                            <Badge key={widx} variant="outline" className="text-xs bg-amber-500/10">
                              {wom}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Works of Mercy */}
      {richContent.works_of_mercy && richContent.works_of_mercy.length > 0 && (
        <Card className="border-amber-500/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Heart className="h-5 w-5 text-amber-500" />
              Works of Mercy Alignment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {richContent.works_of_mercy.map((wom, idx) => (
                <Badge key={idx} className="bg-amber-500/20 text-amber-700 dark:text-amber-300 hover:bg-amber-500/30">
                  {wom}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Strengths & Limitations - Side by Side */}
      {((richContent.strengths && richContent.strengths.length > 0) || 
        (richContent.limitations && richContent.limitations.length > 0)) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {richContent.strengths && richContent.strengths.length > 0 && (
            <Card className="border-emerald-500/20">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ThumbsUp className="h-5 w-5 text-emerald-500" />
                  Strengths
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {richContent.strengths.map((s, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <span className="text-emerald-500">✓</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          {richContent.limitations && richContent.limitations.length > 0 && (
            <Card className="border-orange-500/20">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ThumbsDown className="h-5 w-5 text-orange-500" />
                  Limitations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {richContent.limitations.map((l, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <span className="text-orange-500">•</span>
                      <span>{l}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Privacy Notes */}
      {richContent.privacy_notes && (
        <Card className="border-slate-500/20 bg-slate-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lock className="h-5 w-5 text-slate-500" />
              Privacy & Data Handling
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{richContent.privacy_notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Virtuous Use Tips */}
      {richContent.virtuous_use_tips && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="h-5 w-5 text-primary" />
              Virtuous Use Tips
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{richContent.virtuous_use_tips}</p>
          </CardContent>
        </Card>
      )}

      {/* Integrations */}
      {richContent.integrations && richContent.integrations.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Link2 className="h-5 w-5 text-primary" />
              Integrations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {richContent.integrations.map((integration, idx) => (
                <Badge key={idx} variant="secondary">
                  {integration}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Example Workflows */}
      {richContent.example_workflows && richContent.example_workflows.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Workflow className="h-5 w-5 text-primary" />
              Example Workflows
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {richContent.example_workflows.map((wf, idx) => (
              <div key={idx} className="border-l-2 border-primary/30 pl-4">
                <h4 className="font-medium">{wf.title}</h4>
                <p className="text-sm text-muted-foreground mt-1">{wf.description}</p>
                {wf.steps && wf.steps.length > 0 && (
                  <ol className="list-decimal list-inside mt-2 text-sm space-y-1">
                    {wf.steps.map((step, sidx) => (
                      <li key={sidx}>{step}</li>
                    ))}
                  </ol>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tutorials & Resources */}
      {richContent.tutorials_and_resources && richContent.tutorials_and_resources.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <BookOpen className="h-5 w-5 text-primary" />
              Tutorials & Resources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {richContent.tutorials_and_resources.map((resource, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm">
                  {resource.type && <Badge variant="outline" className="text-xs">{resource.type}</Badge>}
                  {resource.url ? (
                    <a 
                      href={resource.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {resource.title}
                    </a>
                  ) : (
                    <span>{resource.title}</span>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Similar Tools / Competitor Comparison */}
      {richContent.competitor_comparison && richContent.competitor_comparison.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Scale className="h-5 w-5 text-primary" />
              Alternatives & Comparisons
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {richContent.competitor_comparison.map((comp, idx) => (
              <div key={idx} className="border-b border-border last:border-0 pb-3 last:pb-0">
                <h4 className="font-medium text-sm">{comp.competitor}</h4>
                <p className="text-sm text-muted-foreground mt-1">{comp.comparison}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Similar Tools (simple list) */}
      {richContent.similar_tools && richContent.similar_tools.length > 0 && !richContent.competitor_comparison?.length && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Similar Tools</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {richContent.similar_tools.map((tool, idx) => (
                <Badge key={idx} variant="outline">
                  {tool}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RichContentSection;
